import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';

import { createListCollection, EloInputSearch, EloSkeleton } from '@plurall/elo-v3';
import { useQuery } from '@tanstack/react-query';

import { getEscolas } from '@/api/school/schoolApi';
import { useSchoolContext } from '@/features/home-manager';
import { useSchools } from '@/features/home-manager/hooks/useSchools';
import { QueryKeys } from '@/lib/constants/query-keys';
import { debounce } from '@/lib/formatters';
import type { School } from '@/schemas/school';

import { SchoolSelectorRootFactory } from './schoolSelector.factory';

type SchoolSelectorProps = {
  onChange?: (value: string) => void;
};

type SchoolItem = { value: string; label: string };

function toItem(school: School): SchoolItem {
  return {
    value: school.id.toString(),
    label: [school.id, school.nome, school.cidade, school.uf].filter(Boolean).join(' - '),
  };
}

export function SchoolSelector({ onChange }: SchoolSelectorProps): JSX.Element {
  const { schoolId, setSchoolId } = useSchoolContext();
  const [items, setItems] = useState<SchoolItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);

  const { data: defaultData } = useSchools({});
  const defaultItems = useMemo(() => (defaultData ?? []).map(toItem), [defaultData]);

  // When the context school isn't in the default list (e.g. super-admin whose list
  // is huge), fetch it specifically by ID so the selector can pre-select it.
  const schoolInDefault = schoolId ? defaultItems.find((item) => item.value === schoolId) : undefined;
  const needsSpecificFetch = !!schoolId && defaultItems.length > 0 && !schoolInDefault;

  const { data: specificSchoolData } = useQuery({
    queryKey: [QueryKeys.SCHOOLS, { id: schoolId }],
    queryFn: () => getEscolas({ search: schoolId ?? '' }),
    enabled: needsSpecificFetch,
  });

  const initialItem = useMemo(() => {
    if (!schoolId) return defaultItems[0];
    if (schoolInDefault) return schoolInDefault;
    const fromSpecific = specificSchoolData?.find((s) => s.id.toString() === schoolId);
    return fromSpecific ? toItem(fromSpecific) : defaultItems[0];
  }, [schoolId, defaultItems, schoolInDefault, specificSchoolData]);

  // Hold the skeleton until the correct school is resolved
  const isResolved = defaultItems.length > 0 && (!schoolId || !!schoolInDefault || !!specificSchoolData);

  const { data, isFetching } = useQuery({
    queryKey: [QueryKeys.SCHOOLS, { search: searchTerm }],
    queryFn: () => getEscolas({ search: searchTerm }),
    enabled: !!searchTerm,
  });

  useEffect(() => {
    if (!isFetching && data !== undefined) {
      setItems(data.map(toItem));
      setIsLoading(false);
    }
  }, [data, isFetching]);

  const debouncedFetch = useMemo(
    () =>
      debounce((term: string) => {
        setSearchTerm(term);
      }, 350),
    []
  );

  const effectiveItems = searchTerm ? items : defaultItems;
  const collection = useMemo(() => createListCollection({ items: effectiveItems }), [effectiveItems]);

  const handleInputValueChange = useCallback(
    ({ inputValue }: { inputValue: string }) => {
      if (!inputValue.trim()) {
        setItems([]);
        setSearchTerm(undefined);
        setIsLoading(false);
        return;
      }

      const exactMatch = effectiveItems.find((item) => item.label === inputValue.trim());
      if (exactMatch) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      debouncedFetch(inputValue);
    },
    [effectiveItems, debouncedFetch]
  );

  const handleValueChange = useCallback(
    ({ value }: { value: string[] }) => {
      const newVal = value[0] ?? '';
      if (newVal) {
        setSchoolId(newVal);
        onChange?.(newVal);
      }
    },
    [setSchoolId, onChange]
  );

  if (!isResolved) {
    return (
      <SchoolSelectorRootFactory>
        <EloSkeleton.Block
          shape='rectangle'
          style={{ height: '36px', width: '100%', borderRadius: '8px' }}
        />
      </SchoolSelectorRootFactory>
    );
  }

  return (
    <SchoolSelectorRootFactory>
      <EloInputSearch.Root
        collection={collection}
        isLoading={isLoading}
        defaultValue={initialItem ? [initialItem.value] : []}
        defaultInputValue={initialItem?.label}
        onInputValueChange={handleInputValueChange}
        onValueChange={handleValueChange}
        data-test-id='school-selector'>
        <EloInputSearch.Field
          data-test-id='school-search-field'
          placeholder='Buscar escola'
        />
        <EloInputSearch.Content
          emptyText='Nenhuma escola encontrada'
          scrollbarProps={{ maxHeight: 240 }}
          {...(effectiveItems.length === 0 ? { style: { opacity: 0, pointerEvents: 'none', minHeight: 0 } } : {})}>
          {effectiveItems.map((item) => (
            <EloInputSearch.Item
              key={item.value}
              item={item}
              label={item.label}
            />
          ))}
        </EloInputSearch.Content>
      </EloInputSearch.Root>
    </SchoolSelectorRootFactory>
  );
}
